import React, { useState } from "react";
import {
    StyleSheet,
    Text,
    View,
    TouchableOpacity,
    Dimensions,
    TextInput,
} from "react-native";
import Modal from "modal-react-native-web";

const { width, height } = Dimensions.get("window");

export default function DatePicker({ visible, onClose, onSelectDate, currentDate }) {
    const [selectedDate, setSelectedDate] = useState(currentDate || new Date());
    const [year, setYear] = useState(selectedDate.getFullYear());
    const [month, setMonth] = useState(selectedDate.getMonth());

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const handleDateSelect = (day) => {
        const newDate = new Date(year, month, day);
        setSelectedDate(newDate);
    };

    const handleConfirm = () => {
        onSelectDate(selectedDate);
        onClose();
    };

    const renderCalendar = () => {
        const days = [];
        const today = new Date();

        // Empty cells for days before the first day of the month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(
                <View key={`empty-${i}`} style={styles.dayCell}>
                    <Text></Text>
                </View>
            );
        }

        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected = selectedDate.getDate() === day &&
                selectedDate.getMonth() === month &&
                selectedDate.getFullYear() === year;
            const isToday = today.getDate() === day &&
                today.getMonth() === month &&
                today.getFullYear() === year;

            days.push(
                <TouchableOpacity
                    key={day}
                    style={[
                        styles.dayCell,
                        isSelected && styles.selectedDay,
                        isToday && styles.todayDay,
                    ]}
                    onPress={() => handleDateSelect(day)}
                >
                    <Text style={[
                        styles.dayText,
                        isSelected && styles.selectedDayText,
                        isToday && styles.todayText
                    ]}>
                        {day}
                    </Text>
                </TouchableOpacity>
            );
        }

        return days;
    };

    return (
        <Modal
            animationType="slide"
            transparent={true}
            visible={visible}
            onRequestClose={onClose}
        >
            <View style={styles.modalBackground}>
                <View style={styles.calendarContainer}>
                    <Text style={styles.title}>Select Date</Text>

                    {/* Month and Year Navigation */}
                    <View style={styles.headerContainer}>
                        <TouchableOpacity
                            style={styles.navButton}
                            onPress={() => {
                                if (month === 0) {
                                    setMonth(11);
                                    setYear(year - 1);
                                } else {
                                    setMonth(month - 1);
                                }
                            }}
                        >
                            <Text style={styles.navButtonText}>←</Text>
                        </TouchableOpacity>

                        <View style={styles.monthYearContainer}>
                            <Text style={styles.monthYearText}>
                                {months[month]} {year}
                            </Text>
                        </View>

                        <TouchableOpacity
                            style={styles.navButton}
                            onPress={() => {
                                if (month === 11) {
                                    setMonth(0);
                                    setYear(year + 1);
                                } else {
                                    setMonth(month + 1);
                                }
                            }}
                        >
                            <Text style={styles.navButtonText}>→</Text>
                        </TouchableOpacity>
                    </View>

                    {/* Days of week header */}
                    <View style={styles.weekHeader}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <View key={day} style={styles.weekDayCell}>
                                <Text style={styles.weekDayText}>{day}</Text>
                            </View>
                        ))}
                    </View>

                    {/* Calendar Grid */}
                    <View style={styles.calendarGrid}>
                        {renderCalendar()}
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.buttonContainer}>
                        <TouchableOpacity style={styles.cancelButton} onPress={onClose}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.confirmButton} onPress={handleConfirm}>
                            <Text style={styles.confirmButtonText}>Confirm</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    modalBackground: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        justifyContent: "center",
        alignItems: "center",
    },
    calendarContainer: {
        backgroundColor: "#fff",
        borderRadius: 10,
        padding: 20,
        width: width * 0.8,
        maxWidth: 400,
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
    },
    title: {
        fontSize: 20,
        fontFamily: "Prompt-Regular",
        fontWeight: "bold",
        textAlign: "center",
        marginBottom: 15,
        color: "#172B4D",
    },
    headerContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 15,
    },
    navButton: {
        width: 40,
        height: 40,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#F1F3F9",
        borderRadius: 20,
    },
    navButtonText: {
        fontSize: 18,
        fontWeight: "bold",
        color: "#172B4D",
    },
    monthYearContainer: {
        flex: 1,
        alignItems: "center",
    },
    monthYearText: {
        fontSize: 18,
        fontFamily: "Prompt-Regular",
        fontWeight: "bold",
        color: "#172B4D",
    },
    weekHeader: {
        flexDirection: "row",
        marginBottom: 10,
    },
    weekDayCell: {
        flex: 1,
        height: 30,
        justifyContent: "center",
        alignItems: "center",
    },
    weekDayText: {
        fontSize: 14,
        fontFamily: "Prompt-Regular",
        fontWeight: "bold",
        color: "#8898AA",
    },
    calendarGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBottom: 20,
    },
    dayCell: {
        width: "14.28%", // 7 days per week
        height: 40,
        justifyContent: "center",
        alignItems: "center",
        marginBottom: 2,
    },
    dayText: {
        fontSize: 16,
        fontFamily: "Prompt-Regular",
        color: "#172B4D",
    },
    selectedDay: {
        backgroundColor: "#FCC81D",
        borderRadius: 20,
    },
    selectedDayText: {
        color: "#fff",
        fontWeight: "bold",
    },
    todayDay: {
        backgroundColor: "#E3F2FD",
        borderRadius: 20,
    },
    todayText: {
        color: "#2196F3",
        fontWeight: "bold",
    },
    buttonContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 10,
    },
    cancelButton: {
        flex: 1,
        height: 45,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#F1F3F9",
        borderRadius: 5,
        marginRight: 10,
    },
    cancelButtonText: {
        fontSize: 16,
        fontFamily: "Prompt-Regular",
        color: "#172B4D",
    },
    confirmButton: {
        flex: 1,
        height: 45,
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#FCC81D",
        borderRadius: 5,
        marginLeft: 10,
    },
    confirmButtonText: {
        fontSize: 16,
        fontFamily: "Prompt-Regular",
        color: "#fff",
        fontWeight: "bold",
    },
});
